import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const GROUP_BREAK_PREFIX = 'groupBreak_';

export default function AddPricesDialog({ open, onClose, products, commercialOffer: initialCommercialOffer, onSave }) {
  const [items, setItems] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (open && products?.length) {
      const productMap = new Map(products.map((p) => [p.id, { type: 'product', ...p, quantity: p.quantity ?? 1, price: p.price ?? '' }]));
      if (initialCommercialOffer?.groups?.length) {
        const ordered = [];
        initialCommercialOffer.groups.forEach((grp, gi) => {
          grp.items.forEach((it) => {
            const base = productMap.get(it.productId);
            if (base) ordered.push({ ...base, quantity: it.quantity, price: it.price });
          });
          if (gi < initialCommercialOffer.groups.length - 1) {
            ordered.push({ type: 'groupBreak', id: `${GROUP_BREAK_PREFIX}${Date.now()}-${gi}-${Math.random().toString(36).slice(2)}` });
          }
        });
        setItems(ordered.length ? ordered : Array.from(productMap.values()));
      } else {
        setItems(Array.from(productMap.values()));
      }
    } else if (open) {
      setItems([]);
    }
  }, [open, products, initialCommercialOffer]);

  const productItems = items.filter((i) => i.type === 'product');

  const handleQuantityChange = useCallback((id, delta) => {
    setItems((prev) =>
      prev.map((p) => {
        if (p.type !== 'product' || p.id !== id) return p;
        const qty = Math.max(1, (p.quantity ?? 1) + delta);
        return { ...p, quantity: qty };
      })
    );
  }, []);

  const handlePriceChange = useCallback((id, value) => {
    const num = value === '' ? '' : Number(String(value).replace(/,/g, ''));
    setItems((prev) =>
      prev.map((p) =>
        p.type !== 'product' || p.id !== id ? p : { ...p, price: num === '' ? '' : isNaN(num) ? p.price : num }
      )
    );
  }, []);

  const handleRemove = useCallback((id) => {
    setItems((prev) => prev.filter((p) => (p.type === 'product' ? p.id !== id : true)));
  }, []);

  const handleAddGroupBreak = useCallback((afterIndex) => {
    setItems((prev) => {
      const next = [...prev];
      const insertAt = afterIndex + 1;
      const exists = next[insertAt]?.type === 'groupBreak';
      if (exists) return prev;
      next.splice(insertAt, 0, { type: 'groupBreak', id: `${GROUP_BREAK_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}` });
      return next;
    });
  }, []);

  const handleRemoveGroupBreak = useCallback((breakId) => {
    setItems((prev) => prev.filter((p) => p.type !== 'groupBreak' || p.id !== breakId));
  }, []);

  const handleDragStart = useCallback((e, index) => {
    const item = items[index];
    if (item?.type === 'groupBreak') return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.setData('application/json', JSON.stringify({ index, id: item?.id }));
  }, [items]);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(fromIndex) || fromIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    const item = items[fromIndex];
    if (item?.type === 'groupBreak') {
      setDraggedIndex(null);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      next.splice(fromIndex, 1);
      const newDrop = dropIndex > fromIndex ? dropIndex - 1 : dropIndex;
      next.splice(newDrop, 0, item);
      return next;
    });
    setDraggedIndex(null);
  }, [items]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const buildCommercialOffer = useCallback(() => {
    const groups = [];
    let current = [];
    items.forEach((it) => {
      if (it.type === 'groupBreak') {
        if (current.length) {
          const subtotal = current.reduce((s, p) => s + (Number(p.price) || 0) * (p.quantity ?? 1), 0);
          groups.push({
            id: `g${groups.length + 1}`,
            items: current.map((p, i) => ({
              productId: p.id,
              title: p.title || p.productKey,
              quantity: p.quantity ?? 1,
              price: Number(p.price) || 0,
              lineTotal: (Number(p.price) || 0) * (p.quantity ?? 1),
              sno: i + 1,
            })),
            subtotal,
          });
          current = [];
        }
      } else {
        current.push(it);
      }
    });
    if (current.length) {
      const subtotal = current.reduce((s, p) => s + (Number(p.price) || 0) * (p.quantity ?? 1), 0);
      groups.push({
        id: `g${groups.length + 1}`,
        items: current.map((p, i) => ({
          productId: p.id,
          title: p.title || p.productKey,
          quantity: p.quantity ?? 1,
          price: Number(p.price) || 0,
          lineTotal: (Number(p.price) || 0) * (p.quantity ?? 1),
          sno: i + 1,
        })),
        subtotal,
      });
    }
    const grandTotal = groups.reduce((s, g) => s + g.subtotal, 0);
    return { groups, grandTotal };
  }, [items]);

  const handleSave = useCallback(() => {
    const dialogProducts = productItems.map(({ type, ...p }) => ({
      ...p,
      quantity: p.quantity ?? 1,
      price: p.price === '' ? undefined : p.price,
    }));
    const dialogMap = new Map(dialogProducts.map((p) => [p.id, p]));
    const updatedProducts = (products || [])
      .filter((p) => dialogMap.has(p.id))
      .map((p) => {
        const d = dialogMap.get(p.id);
        return { ...p, quantity: d.quantity, price: d.price };
      });
    const commercialOffer = buildCommercialOffer();
    onSave?.({ products: updatedProducts, commercialOffer });
    onClose();
  }, [productItems, products, buildCommercialOffer, onSave, onClose]);

  const totalAmount = productItems.reduce((sum, p) => sum + (Number(p.price) || 0) * (p.quantity ?? 1), 0);

  const groups = [];
  let current = [];
  items.forEach((it) => {
    if (it.type === 'groupBreak') {
      if (current.length) {
        groups.push([...current]);
        current = [];
      }
    } else {
      current.push(it);
    }
  });
  if (current.length) groups.push(current);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
        Add Prices
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add prices, reorder by drag & drop, and create groups for separate tables. Use &quot;Add group&quot; to split into multiple tables.
        </Typography>
        {productItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No products added yet. Add products from the main view first.
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ width: 36 }} />
                    <TableCell sx={{ fontWeight: 600 }}>S.No.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 140 }}>
                      Quantity
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, minWidth: 140 }}>
                      Price (₹)
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>
                      Remove
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Group</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it, idx) => {
                    if (it.type === 'groupBreak') {
                      return (
                        <TableRow key={it.id} sx={{ bgcolor: 'grey.50' }}>
                          <TableCell colSpan={7} sx={{ py: 0.5, borderBottom: '2px dashed', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                — Group break (new table below) —
                              </Typography>
                              <IconButton size="small" onClick={() => handleRemoveGroupBreak(it.id)} sx={{ p: 0.25 }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const globalSno = productItems.findIndex((p) => p.id === it.id) + 1;
                    return (
                      <TableRow
                        key={it.id}
                        hover
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={(e) => handleDrop(e, idx)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          opacity: draggedIndex === idx ? 0.5 : 1,
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                        }}
                      >
                        <TableCell sx={{ py: 0.5, width: 36 }}>
                          <DragIndicatorIcon sx={{ fontSize: 18, color: 'action.disabled' }} />
                        </TableCell>
                        <TableCell>{globalSno}</TableCell>
                        <TableCell>{it.title || it.productKey || '—'}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(it.id, -1)}
                              disabled={(it.quantity ?? 1) <= 1}
                              sx={{ p: 0.25 }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography component="span" sx={{ minWidth: 28, px: 0.5 }}>
                              {it.quantity ?? 1}
                            </Typography>
                            <IconButton size="small" onClick={() => handleQuantityChange(it.id, 1)} sx={{ p: 0.25 }}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="text"
                            placeholder="0"
                            value={it.price === '' ? '' : (Number.isFinite(it.price) ? it.price : '')}
                            onChange={(e) => handlePriceChange(it.id, e.target.value)}
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ width: 120, '& .MuiInputBase-input': { py: 0.75 } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleRemove(it.id)} color="error" sx={{ p: 0.5 }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell align="center" sx={{ width: 80, px: 0 }}>
                          <Button
                            size="small"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => handleAddGroupBreak(idx)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Add group
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {groups.length > 1 ? `${groups.length} groups` : '1 group'} • Drag to reorder
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                Total: ₹ {totalAmount.toLocaleString('en-IN')}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: '#ffbe0b',
            color: '#000814',
            '&:hover': { bgcolor: '#e6ab0a' },
          }}
        >
          Save Prices
        </Button>
      </DialogActions>
    </Dialog>
  );
}
